import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  List, 
  Tag, 
  Space, 
  Typography, 
  Input, 
  Select, 
  Row, 
  Col, 
  Button,
  Empty,
  Spin,
  message,
  Tooltip,
  Badge
} from 'antd';
import { 
  BookOutlined, 
  LinkOutlined, 
  FileTextOutlined, 
  SearchOutlined,
  DownloadOutlined,
  StarOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { usePatientPortal } from '../context/PatientPortalContext';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

// Note: This is used for direct file URLs, not API calls, so we don't add /api/v1 here
const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://care360-backend.onrender.com';

const PatientEducationPortal = () => {
  const { getPortalAxios } = usePatientPortal();
  const [educationMaterials, setEducationMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchText, setSearchText] = useState('');

  // Education categories
  const categories = [
    'General Health',
    'Medications',
    'Procedures',
    'Lifestyle',
    'Nutrition',
    'Exercise',
    'Mental Health',
    'Chronic Conditions',
    'Preventive Care',
    'Post-Procedure Care',
    'Emergency Information',
    'Other'
  ];

  const fetchEducationMaterials = useCallback(async () => {
    setLoading(true);
    try {
      const portalAxios = getPortalAxios();
      let url = '/portal/education';
      if (selectedCategory !== 'all') {
        url += `?category=${selectedCategory}`;
      }
      const response = await portalAxios.get(url);
      setEducationMaterials(response.data.data.educationMaterials || []);
    } catch (error) {
      message.error('Failed to fetch health library materials: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [getPortalAxios, selectedCategory]);

  useEffect(() => {
    fetchEducationMaterials();
  }, [fetchEducationMaterials]);

  // Filter materials based on search text
  const filteredMaterials = educationMaterials.filter(material =>
    material.title.toLowerCase().includes(searchText.toLowerCase()) ||
    material.description?.toLowerCase().includes(searchText.toLowerCase()) ||
    material.category.toLowerCase().includes(searchText.toLowerCase()) ||
    material.tags?.some(tag => tag.toLowerCase().includes(searchText.toLowerCase()))
  );

  // Group materials by category
  const groupedMaterials = filteredMaterials.reduce((acc, material) => {
    const category = material.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(material);
    return acc;
  }, {});

  const getPriorityColor = (priority) => {
    const colors = { 
      Low: 'green', 
      Medium: 'orange', 
      High: 'red', 
      Critical: 'purple' 
    };
    return colors[priority] || 'default';
  };

  const getFileIcon = (documentType, mimeType) => {
    if (documentType === 'link') {
      return <LinkOutlined style={{ color: '#1890ff' }} />;
    }
    
    if (mimeType?.includes('pdf')) {
      return <FileTextOutlined style={{ color: '#ff4d4f' }} />;
    }
    
    return <FileTextOutlined style={{ color: '#52c41a' }} />;
  };

  const handleMaterialClick = (material) => {
    if (material.documentType === 'link') {
      window.open(material.externalLink, '_blank', 'noopener,noreferrer');
    } else {
      // Open file in new tab/page for portal
      const token = localStorage.getItem('portalAuthToken');
      const fileUrl = `${process.env.REACT_APP_API_URL}/api/v1/portal/education/file/${material._id}${token ? `?token=${token}` : ''}`;
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderMaterialCard = (material) => (
    <Card
      key={material._id}
      hoverable
      style={{ marginBottom: 16, cursor: 'pointer' }}
      onClick={() => handleMaterialClick(material)}
      bodyStyle={{ padding: '16px' }}
    >
      <Row gutter={[16, 8]} align="middle">
        <Col flex="none">
          <div style={{ fontSize: '24px' }}>
            {getFileIcon(material.documentType, material.mimeType)}
          </div>
        </Col>
        <Col flex="auto">
          <div>
            <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
              {material.title}
              {material.priority === 'High' || material.priority === 'Critical' ? (
                <StarOutlined style={{ color: '#faad14', marginLeft: 8 }} />
              ) : null}
            </Title>
            {material.description && (
              <Paragraph 
                style={{ margin: 0, marginBottom: 8, color: '#666' }}
                ellipsis={{ rows: 2 }}
              >
                {material.description}
              </Paragraph>
            )}
            <Space wrap>
              <Tag color={getPriorityColor(material.priority)}>{material.priority}</Tag>
              <Tag color="blue">{material.category}</Tag>
              {material.tags?.map(tag => (
                <Tag key={tag} color="default">{tag}</Tag>
              ))}
            </Space>
          </div>
        </Col>
        <Col flex="none">
          <Space direction="vertical" align="end" size="small">
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              {moment(material.uploadDate).format('MM/DD/YYYY')}
            </Text>
            <Tooltip title={material.documentType === 'file' ? "Download/View File" : "Open Link"}>
              <Button 
                type="primary" 
                size="small"
                icon={material.documentType === 'file' ? <DownloadOutlined /> : <LinkOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMaterialClick(material);
                }}
              >
                {material.documentType === 'file' ? 'View' : 'Open'}
              </Button>
            </Tooltip>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  const renderCategorySection = (category, materials) => (
    <div key={category} style={{ marginBottom: 32 }}>
      <Title level={4} style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: 8 }}>
        <BookOutlined style={{ marginRight: 8, color: '#1890ff' }} />
        {category}
        <Badge count={materials.length} style={{ marginLeft: 8 }} />
      </Title>
      {materials.map(renderMaterialCard)}
    </div>
  );

  return (
    <div style={{ padding: '0 16px' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <BookOutlined style={{ marginRight: 12, color: '#1890ff' }} />
          Health Library
        </Title>
        <Paragraph type="secondary">
          Access health library materials and resources provided by your healthcare team to help you
          better understand your health conditions, treatments, and care instructions.
        </Paragraph>
      </div>

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={16}>
            <Search
              placeholder="Search education materials..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              size="large"
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filter by category"
              style={{ width: '100%' }}
              value={selectedCategory}
              onChange={setSelectedCategory}
              size="large"
            >
              <Option value="all">All Categories</Option>
              {categories.map(category => (
                <Option key={category} value={category}>{category}</Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading}>
        {filteredMaterials.length === 0 ? (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  {searchText || selectedCategory !== 'all' 
                    ? 'No education materials found matching your criteria'
                    : 'No education materials available yet'
                  }
                </span>
              }
            >
              {searchText || selectedCategory !== 'all' ? (
                <Button 
                  type="primary" 
                  onClick={() => {
                    setSearchText('');
                    setSelectedCategory('all');
                  }}
                >
                  Clear Filters
                </Button>
              ) : null}
            </Empty>
          </Card>
        ) : selectedCategory === 'all' ? (
          // Show grouped by category when "All Categories" is selected
          Object.entries(groupedMaterials)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([category, materials]) => renderCategorySection(category, materials))
        ) : (
          // Show flat list when specific category is selected
          <div>
            <Title level={4} style={{ marginBottom: 16 }}>
              <BookOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              {selectedCategory}
              <Badge count={filteredMaterials.length} style={{ marginLeft: 8 }} />
            </Title>
            {filteredMaterials.map(renderMaterialCard)}
          </div>
        )}
      </Spin>
    </div>
  );
};

export default PatientEducationPortal;
